import { useBillingServiceStore } from '@billing/AppProvider'
import { GCPrebillingStorageDto } from '@billing/app/api'
import ReportStep from '@billing/app/models/report-step.enum'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useEffect, useMemo } from 'react'
import { StoragePreBillingReportFilter } from '../components/storagePreBillingReport/storagePreBillingReportFilter'
import { ReportNavigationButtonContainer } from './ReportNavigationButtonContainer'

export const GCStoragePreBillingContainer = observer(() => {
  const { reportsUIStore, gcPreBillingStorageUIStore } = useBillingServiceStore()
  const { t } = useTranslate()

  const dataExportConfig = useMemo<IDataExportConfig<any>>(
    () => ({
      date: { header: t('date', 'Date'), type: 'string' },
      customer: { header: t('customer', 'Customer'), type: 'string' },
      commodity: { header: t('commodity', 'Commodity'), type: 'string' },
      carrier: { header: t('carrier', 'Carrier'), type: 'string' },
      arrivalQuantity: { header: t('arrivalQty', 'Arrival Qty'), type: 'number' },
      departureQuantity: { header: t('departureQty', 'Departure Qty'), type: 'number' },
      quantityRemaining: { header: t('qtyBalance', 'Qty Balance'), type: 'number' },
      arrivalWeight: { header: t('arrivalWeight', 'Arrival Weight'), type: 'number' },
      departureWeight: { header: t('departureWeight', 'Departure Weight'), type: 'number' },
      weightRemaining: { header: t('weightBalance', 'Weight Balance'), type: 'number' },
    }),
    [t],
  )

  const dataExport = {
    title: t('gcStoragePreBilling', 'GC Storage Pre-Billing'),
    enableDataExport: true,
    dataExportConfig,
  }

  const columns = useMemo<MRT_ColumnDef<GCPrebillingStorageDto>[]>(
    () => [
      {
        id: 'date',
        header: t('date', 'Date'),
        accessorKey: 'date',
        size: 110,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'customer',
        header: t('customer', 'Customer'),
        accessorKey: 'customer',
        size: 200,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'commodity',
        header: t('commodity', 'Commodity'),
        accessorKey: 'commodity',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'carrier',
        header: t('carrier', 'Carrier'),
        accessorKey: 'carrier',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'arrivalQuantity',
        header: t('arrivalQty', 'Arrival Qty'),
        accessorKey: 'arrivalQuantity',
        size: 100,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'departureQuantity',
        header: t('departureQty', 'Departure Qty'),
        accessorKey: 'departureQuantity',
        size: 100,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'quantityRemaining',
        header: t('qtyBalance', 'Qty Balance'),
        accessorKey: 'quantityRemaining',
        size: 100,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'arrivalWeight',
        header: t('arrivalWeight', 'Arrival Weight'),
        accessorKey: 'arrivalWeight',
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'departureWeight',
        header: t('departureWeight', 'Departure Weight'),
        accessorKey: 'departureWeight',
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'weightRemaining',
        header: t('weightBalance', 'Weight Balance'),
        accessorKey: 'weightRemaining',
        ...tomTableColumnFilterPresets.numericOptional(),
      },
    ],
    [t],
  )

  const defaultOptions = useDefaultMRTOptions<GCPrebillingStorageDto>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'billing/gc-storage-pre-billing',
  )

  const table = useTomTable(
    {
      columns,
      data: gcPreBillingStorageUIStore.reportData,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      enableRowActions: false,
      initialState: {
        ...defaultOptions.initialState,
      },
      muiTableContainerProps: {
        sx: { minHeight: '450px' },
      },
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  const handleDownload = () => {
    const rows = table.getPrePaginationRowModel().rows.map(r => r.original)

    exportToCsv(
      t('gcStorageReport', 'GC Storage Pre-Billing Report'),
      rows,
      dataExportConfig,
      `Storage_${dayjs().format('YYYYMMDD')}.csv`,
    )
  }

  useEffect(() => {
    if (reportsUIStore.currentStep === ReportStep.Report) {
      gcPreBillingStorageUIStore.fetchReportData()
    }
  }, [reportsUIStore.currentStep, gcPreBillingStorageUIStore])

  const finish = () => {
    gcPreBillingStorageUIStore.resetFilter()
    reportsUIStore.resetStepper()
  }

  return (
    <>
      {reportsUIStore.currentStep === ReportStep.Filter && <StoragePreBillingReportFilter />}

      {reportsUIStore.currentStep === ReportStep.Report && (
        <Box sx={{ width: '100%', mt: 1 }}>
          {gcPreBillingStorageUIStore.loadingReport ? (
            <Stack alignItems='center' py={5}>
              <CircularProgress />
            </Stack>
          ) : (
            <TomTable table={table} />
          )}
        </Box>
      )}

      <ReportNavigationButtonContainer
        disableNext={!gcPreBillingStorageUIStore.from && !gcPreBillingStorageUIStore.to}
        downloadAction={handleDownload}
        finishAction={finish}
        disableDownload={
          gcPreBillingStorageUIStore.loadingReport ||
          gcPreBillingStorageUIStore.reportData.length === 0
        }
      />
    </>
  )
})
